import React from 'react';

/**
 * Matches URLs (http, https, or www.) so they can be turned into clickable links.
 * Keeps trailing punctuation out of the link when sensible.
 */
const URL_REGEX = /(https?:\/\/[^\s<>[\]()]+|www\.[^\s<>[\]()]+)/gi;

function normalizeUrl(url: string): string {
  const s = url.trim();
  if (/^www\./i.test(s)) return 'https://' + s;
  if (s.startsWith('/') && typeof window !== 'undefined' && window.location?.origin)
    return window.location.origin + s;
  return s;
}

/**
 * Turns plain text into React nodes with URLs as clickable links that open in a new tab.
 * Use when rendering chat message text so pasted video/media URLs are clickable.
 */
export function linkifyText(text: string): React.ReactNode {
  if (!text || typeof text !== 'string') return text;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(URL_REGEX.source, 'gi');
  while ((m = re.exec(text)) !== null) {
    if (m.index > lastIndex) {
      parts.push(text.slice(lastIndex, m.index));
    }
    const url = m[0];
    const href = normalizeUrl(url);
    parts.push(
      <a
        key={m.index}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          window.open(href, '_blank', 'noopener,noreferrer');
        }}
      >
        {url}
      </a>
    );
    lastIndex = re.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length <= 1 ? (parts[0] ?? text) : <>{parts}</>;
}
